/**
 * React Query hooks for the per-user pins API.
 *
 *   usePins        GET /api/v1/core/me/pins        -> { pins, initialized, ... }
 *   useAddPin      POST /api/v1/core/me/pins        body: { tool_slug }
 *   useRemovePin   DELETE /api/v1/core/me/pins/{slug}
 *
 * Mutations apply an optimistic cache update so the sidebar and the pin
 * toggle on the tool card flip immediately, then reconcile against the
 * server response. On error the optimistic state is rolled back.
 */

import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseMutationResult,
  type UseQueryResult,
} from '@tanstack/react-query';

import { apiDelete, apiGet, apiPost } from './api';
import { API_BASE } from './apiBase';

export const PINS_API_BASE = `${API_BASE}/v1/core/me/pins`;
export const PINS_QUERY_KEY = ['core-me-pins'] as const;

export type PinRow = {
  tool_slug: string;
  sort_order: number;
  created_at: string | null;
};

export type PinsResponse = {
  pins: PinRow[];
  initialized: boolean;
};

export type AddPinResponse = {
  pin: PinRow;
  initialized: true;
};

export type UsePinsResult = {
  pins: PinRow[];
  initialized: boolean;
  pinnedSet: Set<string>;
  isLoading: boolean;
  error: Error | null;
  query: UseQueryResult<PinsResponse, Error>;
};

export function usePins(): UsePinsResult {
  const query = useQuery<PinsResponse, Error>({
    queryKey: PINS_QUERY_KEY,
    queryFn: () => apiGet<PinsResponse>(PINS_API_BASE),
    staleTime: 30_000,
  });
  const pins = query.data?.pins ?? [];
  const initialized = query.data?.initialized ?? false;
  return {
    pins,
    initialized,
    pinnedSet: new Set(pins.map((p) => p.tool_slug)),
    isLoading: query.isLoading,
    error: query.error,
    query,
  };
}

type MutationCtx = { previous: PinsResponse | undefined };

export function useAddPin(): UseMutationResult<AddPinResponse, Error, string, MutationCtx> {
  const qc = useQueryClient();
  return useMutation<AddPinResponse, Error, string, MutationCtx>({
    mutationFn: (toolSlug: string) =>
      apiPost<AddPinResponse>(PINS_API_BASE, { tool_slug: toolSlug }),
    onMutate: async (toolSlug) => {
      await qc.cancelQueries({ queryKey: PINS_QUERY_KEY });
      const previous = qc.getQueryData<PinsResponse>(PINS_QUERY_KEY);
      const current = previous ?? { pins: [], initialized: false };
      if (!current.pins.some((p) => p.tool_slug === toolSlug)) {
        const nextOrder = current.pins.reduce(
          (max, p) => (p.sort_order > max ? p.sort_order : max),
          -1,
        ) + 1;
        const optimistic: PinsResponse = {
          pins: [
            ...current.pins,
            { tool_slug: toolSlug, sort_order: nextOrder, created_at: null },
          ],
          initialized: true,
        };
        qc.setQueryData<PinsResponse>(PINS_QUERY_KEY, optimistic);
      } else {
        qc.setQueryData<PinsResponse>(PINS_QUERY_KEY, {
          ...current,
          initialized: true,
        });
      }
      return { previous };
    },
    onError: (_err, _toolSlug, ctx) => {
      if (ctx?.previous !== undefined) {
        qc.setQueryData(PINS_QUERY_KEY, ctx.previous);
      }
    },
    onSettled: () => {
      void qc.invalidateQueries({ queryKey: PINS_QUERY_KEY });
    },
  });
}

export function useRemovePin(): UseMutationResult<void, Error, string, MutationCtx> {
  const qc = useQueryClient();
  return useMutation<void, Error, string, MutationCtx>({
    mutationFn: (toolSlug: string) =>
      apiDelete<void>(`${PINS_API_BASE}/${encodeURIComponent(toolSlug)}`),
    onMutate: async (toolSlug) => {
      await qc.cancelQueries({ queryKey: PINS_QUERY_KEY });
      const previous = qc.getQueryData<PinsResponse>(PINS_QUERY_KEY);
      const current = previous ?? { pins: [], initialized: false };
      const optimistic: PinsResponse = {
        pins: current.pins.filter((p) => p.tool_slug !== toolSlug),
        initialized: true,
      };
      qc.setQueryData<PinsResponse>(PINS_QUERY_KEY, optimistic);
      return { previous };
    },
    onError: (_err, _toolSlug, ctx) => {
      if (ctx?.previous !== undefined) {
        qc.setQueryData(PINS_QUERY_KEY, ctx.previous);
      }
    },
    onSettled: () => {
      void qc.invalidateQueries({ queryKey: PINS_QUERY_KEY });
    },
  });
}
